import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  resumeStepUpRequest,
  type StepUpResumeActionId,
  type StepUpResumeOutcome,
  type StepUpRetryOutcome,
} from "@/lib/api";
import { takePendingStepUpResume, type PendingStepUpResume } from "@/lib/step-up-resume";
import type { StepUpLocaleCatalog } from "@/i18n/use-step-up-locale";

/**
 * The UI side of the step-up resume (ALL-5021/ALL-5022).
 *
 * A write that could not be authorized on the current session is parked in
 * sessionStorage with its step-up challenge and sent through a step-up
 * re-authentication; the page named by the record's `returnPath` mounts this
 * hook, which completes that same challenge and re-issues the request exactly
 * once with the request's own idempotency key.
 *
 * `actions` scopes the hook to the operations the mounting surface owns. Each
 * surface resumes only its own write — two hooks can therefore live on one page
 * (user detail has one for suspend and one for the role change) without either
 * stealing the other's record — and a record naming an unknown action is refused
 * rather than dispatched (lib/api STEP_UP_RESUME_ACTIONS).
 */
export interface StepUpResumeFailure {
  /** `expired`: the stored challenge was discarded before it could be used, so
   * nothing was replayed. `failed`: the challenge completion or the replayed
   * request was refused. */
  kind: "expired" | "failed";
  action?: string;
  /** RFC 9457 detail from the server, when it wrote one. */
  error?: string;
  reasonCode?: string;
  supportReference?: string;
  capability?: string;
}

export interface UseStepUpResumeOptions {
  actions: readonly StepUpResumeActionId[];
  onResuming?: (record: PendingStepUpResume) => void;
  /** The replayed request succeeded; `outcome` is its own response, so callers
   * can carry its support reference into their success state. */
  onResumed?: (record: PendingStepUpResume, outcome: StepUpResumeOutcome) => void;
  onFailed?: (failure: StepUpResumeFailure) => void;
}

export function useStepUpResume(options: UseStepUpResumeOptions): { resuming: boolean } {
  // Callbacks are read through a ref so a caller that re-renders mid-resume does
  // not restart it, and so the effect can depend on the action list alone.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const [resuming, setResuming] = useState(false);
  const actionsKey = options.actions.join("|");
  // The record is acted on only by the page it names (its returnPath), so a hook
  // on another page — the same surface for a different user, say — leaves it be.
  const { pathname } = useLocation();

  useEffect(() => {
    const pending = takePendingStepUpResume(actionsKey ? actionsKey.split("|") : [], Date.now(), pathname);
    if (pending.status === "none") return;
    if (pending.status === "expired") {
      optionsRef.current.onFailed?.({ kind: "expired" });
      return;
    }
    const record = pending.record;
    setResuming(true);
    optionsRef.current.onResuming?.(record);
    // Deliberately not cancelled on cleanup: the record is already consumed, so
    // dropping the work would lose the user's write. A second effect run (React
    // StrictMode) finds no record and does nothing, and the callbacks below fire
    // exactly once for the one run that claimed it.
    void (async () => {
      const { completed, outcome } = await resumeStepUpRequest(record);
      setResuming(false);
      if (!completed.ok) {
        optionsRef.current.onFailed?.({
          kind: "failed",
          action: record.action,
          error: completed.error,
          reasonCode: completed.reasonCode,
          supportReference: completed.supportReference,
          capability: completed.capability,
        });
        return;
      }
      if (!outcome || !outcome.ok) {
        optionsRef.current.onFailed?.({
          kind: "failed",
          action: record.action,
          error: outcome?.error,
          reasonCode: outcome?.reasonCode,
          supportReference: outcome?.supportReference ?? completed.supportReference,
          capability: outcome?.capability,
        });
        return;
      }
      optionsRef.current.onResumed?.(record, outcome);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionsKey, pathname]);

  return { resuming };
}

/**
 * What a page shows when withStepUpRetry returns without starting a redirect:
 * the server's own refusal when there is one, else the catalogue's copy for
 * "this action needs a fresh sign-in and we could not start one". Shared by
 * every caller so the two admin surfaces cannot drift apart.
 */
export function describeStepUpOutcome<
  T extends { ok: boolean; error?: string; reasonCode?: string; supportReference?: string; capability?: string },
>(
  outcome: StepUpRetryOutcome<T>,
  catalog: StepUpLocaleCatalog,
): { message: string; supportReference?: string; capability?: string } {
  if (outcome.stepUpFailed) {
    return {
      message: catalog.ui["stepUp.unavailable"],
      supportReference: outcome.stepUpSupportReference ?? outcome.result.supportReference,
      capability: outcome.result.capability,
    };
  }
  return {
    message: outcome.result.error ?? catalog.ui["stepUp.failedDetail"],
    supportReference: outcome.result.supportReference,
    capability: outcome.result.capability,
  };
}

/** The same shape for a resume that ran on the way back from re-authentication
 * and did not succeed: nothing was applied, and the reference below is what
 * support can trace it by. */
export function describeStepUpResumeFailure(
  failure: StepUpResumeFailure,
  catalog: StepUpLocaleCatalog,
): { title: string; message: string; supportReference?: string; capability?: string } {
  return {
    title: catalog.ui["stepUp.failedTitle"],
    message: failure.kind === "expired"
      ? catalog.ui["stepUp.expired"]
      : failure.error ?? catalog.ui["stepUp.failedDetail"],
    supportReference: failure.supportReference,
    capability: failure.capability,
  };
}
